// Shared Framer Motion contract for idukki-web.
//
// One source of truth for easing, springs and the reveal/stagger
// variants, so every component animates in the same Emil-register voice
// (spring or the brand curve, transform/opacity only, small travel). Pure
// data + type imports, so this file is safe to import from server or
// client modules; the hooks live in the components that consume it.
//
// See .claude/skills/framer-motion/SKILL.md for the full rules.

import type { Variants, Transition } from 'framer-motion'

/** The brand cubic-bezier (matches --ease in redesign-theme.css). */
export const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1]

/** Crisp spring for interactive + entrance motion. */
export const SPRING: Transition = { type: 'spring', stiffness: 380, damping: 32 }
/** Softer spring for larger objects settling in (hero phone, panels). */
export const SPRING_SOFT: Transition = { type: 'spring', stiffness: 240, damping: 30 }

/** Section / block fade-up. ~24px travel, 500ms on the brand curve. */
export const fadeUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
}

/** Container that staggers its children's reveal. Pair with fadeUpItem. */
export const staggerContainer: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.04 } },
}

/** Child of a staggerContainer. Smaller travel so a list settles, not slides. */
export const fadeUpItem: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: EASE } },
}

/** Settle-in for cards / media (scale 0.96 -> 1 + fade). */
export const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.96 },
  show: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: EASE } },
}

/** Standard viewport trigger: fire once, just before fully in view. */
export const VIEWPORT = { once: true, margin: '0px 0px -10% 0px' } as const
